import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  Copy,
  ExternalLink,
  LogOut,
  Ban,
  ChevronDown,
  Apple,
  Monitor,
  Smartphone,
  Terminal,
  Inbox,
  Settings as SettingsIcon,
} from "lucide-react";
import { getCurrentPrincipal, logout } from "@/api/client";
import {
  useMe,
  useMeSubscription,
  useSelfIssueProfile,
  useSelfRevokeProfile,
} from "@/api/queries";
import type { MePortalProfile } from "@/api/types";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardBody,
  CardHeader,
  CardTitle,
  CardDescription,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Dialog } from "@/components/ui/dialog";
import { cn } from "@/lib/cn";

/**
 * /my — личный кабинет сотрудника (Phase 3.1).
 *
 * Показывает все vpn_profiles текущего employee. Если 0 профилей —
 * подсказка обратиться к админу. Если несколько (например, после revoke +
 * перевыдача) — выбор активного через табы; remained отображается в audit
 * как «отозван».
 */
export function PortalHome() {
  const principal = getCurrentPrincipal();
  const me = useMe();
  const [activeId, setActiveId] = useState<string | null>(null);
  const [revokeOpen, setRevokeOpen] = useState(false);

  const profiles = me.data?.vpn_profiles ?? [];
  const selectedId = activeId ?? profiles.find((p) => p.status === "active")?.id ?? profiles[0]?.id ?? null;

  return (
    <div className="min-h-screen py-12 px-4">
      <div className="max-w-3xl mx-auto space-y-6">
        <header className="flex items-center justify-between gap-2">
          <div>
            <div className="font-display text-2xl font-bold">Signaris Net</div>
            <div className="text-sm text-fg-muted">{principal?.email}</div>
          </div>
          <div className="flex items-center gap-2">
            {/* Если у юзера есть админ-роль (tenant_owner / net:admin) —
                даём ссылку обратно в админку. Обычным user'ам кнопку не
                показываем — у них admin-доступа нет, попытка ходит → 403. */}
            {(principal?.is_superadmin ||
              principal?.net_role === "tenant_owner" ||
              principal?.net_role === "admin") && (
              <Link
                to="/admin"
                className="inline-flex items-center justify-center gap-2 font-medium transition-all border border-border hover:bg-white/5 text-fg h-8 px-3 text-sm rounded-lg"
              >
                <SettingsIcon size={14} /> В админку
              </Link>
            )}
            <Button variant="outline" size="sm" onClick={() => logout(true)}>
              <LogOut size={14} /> Выйти
            </Button>
          </div>
        </header>

        {me.isLoading ? (
          <Card>
            <CardBody>
              <div className="text-fg-muted text-sm">Загрузка…</div>
            </CardBody>
          </Card>
        ) : profiles.length === 0 ? (
          <NoProfilesCard />
        ) : (
          <>
            {profiles.length > 1 && (
              <ProfileTabs
                profiles={profiles}
                selectedId={selectedId}
                onSelect={setActiveId}
              />
            )}
            {selectedId && (
              <ProfileSection
                profileId={selectedId}
                profile={profiles.find((p) => p.id === selectedId)!}
                onRevokeClick={() => setRevokeOpen(true)}
              />
            )}
          </>
        )}
      </div>

      <Dialog
        open={revokeOpen}
        onClose={() => setRevokeOpen(false)}
        title="Отозвать свой ключ?"
        description="Вы потеряете доступ к VPN в течение 30 секунд. Для восстановления обратитесь к администратору."
      >
        <RevokeBody
          profileId={selectedId}
          onDone={() => setRevokeOpen(false)}
        />
      </Dialog>
    </div>
  );
}

function NoProfilesCard() {
  const issue = useSelfIssueProfile();

  const onIssue = () => {
    issue.mutate(undefined, {
      onSuccess: () => toast.success("VPN-конфиг выпущен"),
      onError: (e: unknown) => {
        const msg =
          (e as { response?: { data?: { detail?: string } } }).response?.data
            ?.detail ?? "Не удалось выпустить конфиг";
        toast.error(msg);
      },
    });
  };

  return (
    <Card>
      <CardBody className="text-center py-12 space-y-4">
        <Inbox size={36} className="mx-auto text-fg-subtle" />
        <div className="font-medium">У вас ещё нет VPN-профиля</div>
        <div className="text-sm text-fg-muted max-w-md mx-auto">
          Нажмите кнопку ниже — мы выпустим вам конфиг автоматически.
          После этого появятся sub URL, QR-код и инструкции по установке.
        </div>
        <Button onClick={onIssue} disabled={issue.isPending}>
          {issue.isPending ? "Выпускаем…" : "Запросить VPN"}
        </Button>
      </CardBody>
    </Card>
  );
}

function ProfileTabs({
  profiles,
  selectedId,
  onSelect,
}: {
  profiles: MePortalProfile[];
  selectedId: string | null;
  onSelect: (id: string) => void;
}) {
  return (
    <div className="flex gap-2 overflow-x-auto">
      {profiles.map((p) => {
        const active = p.id === selectedId;
        return (
          <button
            key={p.id}
            type="button"
            onClick={() => onSelect(p.id)}
            className={cn(
              "px-3 py-2 rounded-lg border text-sm whitespace-nowrap",
              active
                ? "border-accent bg-accent-subtle text-accent"
                : "border-border text-fg-muted hover:bg-white/[0.04]",
            )}
          >
            <span className="font-mono text-xs">{p.vpn_email}</span>
            <Badge
              tone={p.status === "active" ? "success" : "danger"}
              className="ml-2"
            >
              {p.status}
            </Badge>
          </button>
        );
      })}
    </div>
  );
}

function ProfileSection({
  profileId,
  profile,
  onRevokeClick,
}: {
  profileId: string;
  profile: MePortalProfile;
  onRevokeClick: () => void;
}) {
  const isActive = profile.status === "active";
  // Подписку грузим только для активного профиля — у отозванного sub URL
  // ведёт на пустую подписку, показывать его QR/ссылку нельзя.
  const sub = useMeSubscription(isActive ? profileId : null);

  if (!isActive) {
    return <RevokedCard />;
  }

  return (
    <>
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between gap-4">
            <div>
              <CardTitle>Ваш VPN-ключ</CardTitle>
              <CardDescription>
                Добавьте ссылку как <strong>подписку</strong> в VPN-клиент —
                обновления серверов подтянутся автоматически.
              </CardDescription>
            </div>
            <Badge tone="success">{profile.status}</Badge>
          </div>
        </CardHeader>
        <CardBody className="space-y-6">
          {sub.isLoading || !sub.data ? (
            <div className="text-sm text-fg-muted">Загрузка подписки…</div>
          ) : (
            <>
              <div>
                <span className="label">Subscription URL</span>
                <div className="flex gap-2 mt-1">
                  <Input readOnly value={sub.data.subscription_url} />
                  <Button
                    variant="outline"
                    onClick={() => {
                      navigator.clipboard.writeText(sub.data!.subscription_url);
                      toast.success("Ссылка скопирована");
                    }}
                  >
                    <Copy size={14} /> Скопировать
                  </Button>
                </div>
              </div>

              <div className="flex flex-col items-start gap-3">
                <div className="label">
                  QR-код (для Happ / v2rayNG / Hiddify на телефоне)
                </div>
                <div className="p-4 bg-white rounded-xl">
                  <img
                    src={sub.data.qr_base64}
                    alt="QR подписки"
                    className="w-48 h-48 block"
                  />
                </div>
              </div>
            </>
          )}
        </CardBody>
      </Card>

      <InstallCard subscriptionUrl={sub.data?.subscription_url ?? ""} />

      <Card>
        <CardHeader>
          <CardTitle>Безопасность</CardTitle>
        </CardHeader>
        <CardBody>
          <Button variant="danger" onClick={onRevokeClick}>
            <Ban size={14} /> Отозвать мой ключ
          </Button>
          <p className="text-xs text-fg-subtle mt-2">
            Если ключ скомпрометирован — отзовите его, и обратитесь к админу.
          </p>
        </CardBody>
      </Card>
    </>
  );
}

function RevokedCard() {
  const issue = useSelfIssueProfile();

  const onIssue = () => {
    issue.mutate(undefined, {
      onSuccess: () => toast.success("Новый VPN-конфиг выпущен"),
      onError: (e: unknown) => {
        const msg =
          (e as { response?: { data?: { detail?: string } } }).response?.data
            ?.detail ?? "Не удалось выпустить конфиг";
        toast.error(msg);
      },
    });
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between gap-4">
          <CardTitle>Ключ отозван</CardTitle>
          <Badge tone="danger">revoked</Badge>
        </div>
      </CardHeader>
      <CardBody className="space-y-4">
        <p className="text-sm text-fg-muted">
          Этот VPN-ключ отозван и больше не работает. Выпустите новый или
          обратитесь к администратору.
        </p>
        <Button onClick={onIssue} disabled={issue.isPending}>
          {issue.isPending ? "Выпускаем…" : "Запросить новый VPN"}
        </Button>
      </CardBody>
    </Card>
  );
}

function RevokeBody({
  profileId,
  onDone,
}: {
  profileId: string | null;
  onDone: () => void;
}) {
  const revoke = useSelfRevokeProfile();
  const [err, setErr] = useState<string | null>(null);

  const submit = async () => {
    if (!profileId) return;
    setErr(null);
    try {
      await revoke.mutateAsync(profileId);
      toast.success("Ключ отозван");
      onDone();
    } catch (e: unknown) {
      const detail =
        (e as { response?: { data?: { detail?: string } } }).response?.data
          ?.detail ?? "Ошибка";
      setErr(detail);
      toast.error(detail);
    }
  };

  return (
    <div className="flex flex-col gap-3">
      {err && <div className="text-sm text-state-danger">{err}</div>}
      <div className="flex justify-end gap-2">
        <Button variant="outline" onClick={onDone}>
          Отмена
        </Button>
        <Button variant="danger" onClick={submit} disabled={revoke.isPending}>
          Да, отозвать
        </Button>
      </div>
    </div>
  );
}

// ─── Install instructions ────────────────────────────────────────────────

interface OSOption {
  id: string;
  label: string;
  icon: typeof Monitor;
  app: string;
  downloadUrl: string;
  steps: string[];
}

const OS_OPTIONS: OSOption[] = [
  {
    id: "windows",
    label: "Windows",
    icon: Monitor,
    app: "v2rayN",
    downloadUrl: "https://github.com/2dust/v2rayN/releases/latest",
    steps: [
      "Скачайте v2rayN по ссылке ниже (архив v2rayN-With-Core.zip).",
      "Распакуйте архив и запустите v2rayN.exe.",
      "Скопируйте Subscription URL из блока выше.",
      "В v2rayN: Subscription → Subscription settings → Add → вставьте URL в поле «URL».",
      "Subscription → Update subscription (without proxy).",
      "Выберите любой из серверов → правая кнопка → Set as active server.",
      "Нажмите на иконку в трее → «Auto proxy mode (PAC)».",
    ],
  },
  {
    id: "macos",
    label: "macOS",
    icon: Apple,
    app: "Hiddify",
    downloadUrl: "https://github.com/hiddify/hiddify-next/releases/latest",
    steps: [
      "Скачайте Hiddify-Next для macOS (файл *.dmg).",
      "Откройте .dmg и перетащите Hiddify в Applications.",
      "Запустите Hiddify → «+» → «Add from Clipboard»: сначала скопируйте Subscription URL из блока выше.",
      "Или «Add from URL» и вставьте ссылку.",
      "Нажмите на переключатель Connect — готово.",
    ],
  },
  {
    id: "linux",
    label: "Linux",
    icon: Terminal,
    app: "Hiddify / v2rayA",
    downloadUrl: "https://github.com/hiddify/hiddify-next/releases/latest",
    steps: [
      "Скачайте Hiddify-Next AppImage для Linux x86_64.",
      "chmod +x Hiddify-*.AppImage && ./Hiddify-*.AppImage",
      "В Hiddify: «+» → «Add from URL» → вставьте Subscription URL.",
      "Нажмите Connect.",
    ],
  },
  {
    id: "ios",
    label: "iOS",
    icon: Smartphone,
    app: "Happ",
    downloadUrl: "https://apps.apple.com/us/app/happ-proxy-utility/id6504287215",
    steps: [
      "Установите Happ из App Store. В российском App Store его нет — нужен Apple ID другой страны.",
      "Откройте Happ → «+» в правом верхнем углу → сканирование QR-кода.",
      "Отсканируйте QR-код выше. Если эта страница открыта на самом iPhone, скопируйте Subscription URL и добавьте подписку из буфера обмена.",
      "Выберите сервер и подключитесь. Правило «Россия напрямую» Happ получит вместе с подпиской.",
    ],
  },
  {
    id: "android",
    label: "Android",
    icon: Smartphone,
    app: "Happ",
    downloadUrl: "https://github.com/Happ-proxy/happ-android/releases/latest/download/Happ.apk",
    steps: [
      "Скачайте Happ.apk по ссылке ниже или установите Happ из Google Play.",
      "Установите APK (разрешите установку из неизвестных источников).",
      "Откройте Happ → «+» в правом верхнем углу → сканирование QR-кода.",
      "Отсканируйте QR-код выше. Если эта страница открыта на самом телефоне, скопируйте Subscription URL и добавьте подписку из буфера обмена.",
      "Выберите сервер и подключитесь. Правило «Россия напрямую» Happ получит вместе с подпиской.",
    ],
  },
];

function InstallCard({ subscriptionUrl: _subscriptionUrl }: { subscriptionUrl: string }) {
  const [openId, setOpenId] = useState<string | null>(null);
  return (
    <Card>
      <CardHeader>
        <CardTitle>Установка на устройство</CardTitle>
        <CardDescription>
          Выберите ОС — появятся пошаговые инструкции
        </CardDescription>
      </CardHeader>
      <CardBody>
        <div className="space-y-2">
          {OS_OPTIONS.map((os) => {
            const Icon = os.icon;
            const isOpen = openId === os.id;
            return (
              <div
                key={os.id}
                className="rounded-xl border border-border overflow-hidden"
              >
                <button
                  type="button"
                  onClick={() => setOpenId(isOpen ? null : os.id)}
                  className={cn(
                    "w-full flex items-center gap-3 px-4 py-3 text-left",
                    "hover:bg-white/[0.03] transition-colors",
                    isOpen && "bg-white/[0.03]",
                  )}
                >
                  <Icon size={16} className="text-fg-subtle shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="font-medium">{os.label}</div>
                    <div className="text-xs text-fg-subtle">
                      через {os.app}
                    </div>
                  </div>
                  <ChevronDown
                    size={16}
                    className={cn(
                      "text-fg-subtle shrink-0 transition-transform",
                      isOpen && "rotate-180",
                    )}
                  />
                </button>
                {isOpen && (
                  <div className="px-4 pb-4 pt-1 space-y-3 border-t border-border bg-white/[0.01]">
                    <ol className="space-y-2 text-sm text-fg-muted list-decimal list-inside marker:text-fg-subtle">
                      {os.steps.map((s, i) => (
                        <li key={i}>{s}</li>
                      ))}
                    </ol>
                    <a
                      href={os.downloadUrl}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex items-center gap-1.5 text-sm text-accent hover:underline"
                    >
                      Скачать {os.app}
                      <ExternalLink size={12} />
                    </a>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </CardBody>
    </Card>
  );
}
